// Who is signed in, and the exchanges that get them there: a login — the phone
// number, or a username — with a password, or a registration.
//
// A learner staff added is handed a password for the number staff typed in,
// and signs in with the two. The number is how people are found and added; the
// password is what proves who they are.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { ApiError, api, handlePasswordChangeRequired, handleSessionReplaced, handleSignedOut, post } from './api'
import type { Identity, User } from './api'

/**
 * Why this browser was signed out, when that is worth telling the learner:
 * 'replaced' — they signed in on another device, and the platform keeps a
 * learner to one session at a time, so the server ended this one and answers
 * its cookie with `session_replaced`. Any other end of a session (signing out,
 * an expired session, a changed or reset password) has nothing to explain.
 */
export type SignedOutReason = 'replaced'

/** What the register form sends. The keys are the API's own, so a per-field
 *  complaint comes back under the name the form already knows the input by. */
export type RegisterInput = {
  full_name: string
  /** Any spelling the server's NormalisePhone accepts; the form sends +998XXXXXXXXX. */
  phone: string
  /** Optional: the number is what signs a learner in, a username only a second way. */
  username?: string
  password: string
}

type Auth = {
  user: User | null
  /**
   * True while the password the learner signed in with is one somebody else
   * chose — staff who enrolled them or reset it. The server refuses everything
   * else until they choose their own, and App shows nothing but the page where
   * they do.
   */
  mustChangePassword: boolean
  /** False only while the first /me call is in flight; screens wait on this. */
  ready: boolean
  /**
   * Why the last session in this browser ended, when the sign-in page should
   * say so; null otherwise. Set by any call, quiet or not, that comes back
   * `session_replaced` — the first quiet /auth/me of a PC reopened after the
   * learner signed in on the phone, a lesson's progress report, the language
   * saved to the account, the re-check when the tab is looked at again — and
   * kept for the whole browser (localStorage, see SIGNED_OUT_REASON): every
   * tab shares the one session cookie, so every tab is told, and a reload of
   * the sign-in page still explains it. The next identity the server confirms
   * (a sign-in, a registration, /me answering, a sign-out the server
   * accepted) clears it.
   */
  signedOutReason: SignedOutReason | null
  /**
   * Signs in with a login — a phone number in any spelling, or a username —
   * and a password, or throws the ApiError the form explains. The server
   * decides which of the two the login is.
   */
  signIn: (login: string, password: string) => Promise<User>
  register: (input: RegisterInput) => Promise<User>
  /**
   * Signs out of this browser. Given `cause` — the 401 a quiet call kept to
   * itself, meaning the server has already ended the session — it asks the
   * server nothing more and remembers why (see signedOutReason).
   */
  signOut: (cause?: unknown) => Promise<void>
  /**
   * Asks /auth/me again, takes whatever it says — who is signed in, and
   * whether they still have to choose a password — and resolves to that
   * answer. Calls made while one is in flight share it, unless `fresh` is
   * asked for: then an answer already on its way, which may have been asked
   * before whatever the caller has just learnt, is dropped and a new question
   * is put.
   */
  refresh: (opts?: { fresh?: boolean }) => Promise<Identity>
  /**
   * Called once POST /auth/password has succeeded. The server lifts the flag
   * in the same transaction that stores the new password, so it is lifted here
   * at once, and /auth/me is asked afresh for the rest — an answer already on
   * its way was asked before the change and is dropped. Other tabs of this
   * site are told too (see PASSWORD_CHOSEN).
   */
  passwordChanged: () => Promise<Identity>
}

/**
 * The browser-storage key one tab writes when it has chosen the password, so
 * that another tab of the same site still showing the "choose your own
 * password" page asks /auth/me again at once instead of waiting to be used.
 * The admin panel writes the same key: on a tenant's host both apps share one
 * origin and one session cookie, so a password chosen in either is chosen for
 * both. What is written is a time, only so that each write is a change.
 */
export const PASSWORD_CHOSEN = 'mymentor.password-chosen'

/**
 * Where this browser keeps signedOutReason: localStorage, so a reload of the
 * sign-in page still explains it, and so the other tabs — which share the
 * session cookie, and so the ended session — hear of it through the 'storage'
 * event. Only ever "replaced:<time>" or absent, kept for a day at most
 * (REASON_TTL_MS), and written only when that changes: each write is an event
 * in every other tab, and a tab that hears one may ask the server (see the
 * listener in AuthProvider).
 */
const SIGNED_OUT_REASON = 'mymentor.signed-out-reason'

/**
 * How often, at most, a signed-in tab asks /auth/me again when it is looked at
 * (focus, or becoming visible). A learner who signed in on the phone finds the
 * PC signed out — and told why — when they next turn to it, not only when they
 * next click something; more often than this would be traffic for nothing.
 */
const RECHECK_EVERY_MS = 30_000

const AuthContext = createContext<Auth | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [mustChangePassword, setMustChangePassword] = useState(false)
  const [ready, setReady] = useState(false)
  const [signedOutReason, setSignedOutReason] = useState<SignedOutReason | null>(readReason)

  /** Records why the session ended (or that nothing needs saying), here and for a reload. */
  const remember = useCallback((reason: SignedOutReason | null) => {
    setSignedOutReason(reason)
    keepReason(reason)
  }, [])

  // Bumped whenever the person behind this window changes — signing in,
  // registering, signing out, a session found dead — and when the password is
  // chosen. An answer to /me asked for before the bump describes a state that
  // is gone, and is dropped: without this, a refresh that set off just before
  // "Sign out" could land after it and sign the learner straight back in on
  // screen.
  const epoch = useRef(0)
  const refreshing = useRef<Promise<Identity> | null>(null)

  const apply = useCallback(
    (res: Identity) => {
      setUser(res.user)
      setMustChangePassword(res.must_change_password === true)
      // Signed in: whatever ended an earlier session is history.
      remember(null)
    },
    [remember],
  )

  /** A new person, or nobody: whatever was being asked about the last one is moot. */
  const turn = useCallback(() => {
    epoch.current++
    refreshing.current = null
  }, [])

  /**
   * Nobody is signed in here any more. `cause` is the refusal that said so,
   * when there was one; a sign-in on another device is remembered, so the
   * sign-in page can say why. Any other cause leaves the reason as it was: a
   * plain 401 can follow the replaced one (a server that clears the cookie
   * with that answer leaves the requests still on their way without one, and
   * another tab may have heard the news first), and it must not wipe the one
   * explanation the learner is owed.
   */
  const forget = useCallback(
    (cause?: unknown) => {
      turn()
      setUser(null)
      setMustChangePassword(false)
      if (cause instanceof ApiError && cause.isSessionReplaced) remember('replaced')
    },
    [turn, remember],
  )

  useEffect(() => {
    const ac = new AbortController()
    // `quiet` matters here: an anonymous first visit is the normal case, not a
    // session that just expired, and it must not trigger the signed-out path.
    //
    // A request this effect abandoned says nothing. Answering "nobody" for it
    // would decide the page before the real answer arrives: the router sends
    // the learner to the sign-in screen, and from there, once the answer does
    // arrive, to the home page instead of the page they asked for — which is
    // what a reload under StrictMode, whose effects run twice, did every time.
    //
    // Quiet, but not deaf: a PC opened again after the learner signed in on
    // their phone is answered session_replaced, and the sign-in page it lands
    // on says why.
    api<Identity>('/auth/me', { signal: ac.signal, quiet: true })
      .then((res) => {
        if (!ac.signal.aborted) apply(res)
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted) return
        setUser(null)
        if (err instanceof ApiError && err.isSessionReplaced) remember('replaced')
      })
      .finally(() => {
        if (!ac.signal.aborted) setReady(true)
      })
    return () => ac.abort()
  }, [apply, remember])

  const refresh = useCallback(
    (opts?: { fresh?: boolean }) => {
      // A fresh question makes any answer still on its way moot, exactly as a
      // new person does: it was asked before whatever made the caller ask again.
      if (opts?.fresh) turn()
      else if (refreshing.current) return refreshing.current
      const asked = epoch.current
      // Not quiet: this is only ever asked by somebody signed in, so a 401 here
      // is a session that has ended, and the shared handling says so.
      const p: Promise<Identity> = api<Identity>('/auth/me')
        .then((res) => {
          if (epoch.current === asked) apply(res)
          return res
        })
        .finally(() => {
          if (refreshing.current === p) refreshing.current = null
        })
      refreshing.current = p
      return p
    },
    [apply, turn],
  )

  const passwordChanged = useCallback(() => {
    turn()
    setMustChangePassword(false)
    try {
      localStorage.setItem(PASSWORD_CHOSEN, String(Date.now()))
    } catch {
      // Storage switched off: the other tabs find out when they are next used.
    }
    return refresh()
  }, [turn, refresh])

  useEffect(() => {
    handleSignedOut(forget)
    // A session ended by a sign-in on another device is recorded whichever
    // call hears it, quiet or not. Only recorded: a quiet call's caller
    // decides what its 401 does to the page, and a loud one reaches forget
    // above as well.
    handleSessionReplaced(() => remember('replaced'))
    // Any refusal saying the password must be chosen first is a reason to ask
    // who is here again; the answer is what switches the page. A refresh that
    // fails leaves things as they are — the refusal itself is still shown by
    // whatever made the request.
    handlePasswordChangeRequired(() => {
      refresh().catch(() => undefined)
    })
  }, [forget, remember, refresh])

  // A tab left on the "choose your own password" page while the password was
  // chosen somewhere else — another tab of this browser, which shares the
  // session — would otherwise go on asking for a password that is no longer
  // the one in force. So while the page is up, /auth/me is asked again when
  // another tab says it has chosen one, and whenever this tab is looked at
  // again; the answer takes the page away if the flag is gone.
  useEffect(() => {
    if (!mustChangePassword) return
    const ask = () => {
      refresh().catch(() => undefined)
    }
    const onStorage = (e: StorageEvent) => {
      if (e.key === PASSWORD_CHOSEN) ask()
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') ask()
    }
    window.addEventListener('storage', onStorage)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('storage', onStorage)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [mustChangePassword, refresh])

  /**
   * Asks /auth/me again, for a tab that believes somebody is signed in.
   * Quiet: the answer is handled here. An ended session signs this tab out
   * with its reason; an answer takes whatever has changed (a password reset by
   * staff in the meantime brings up the "choose your own" page); a failure to
   * ask at all — offline, say — changes nothing. An answer asked before the
   * person here changed is dropped, as refresh() drops one.
   */
  const recheck = useCallback(() => {
    const asked = epoch.current
    api<Identity>('/auth/me', { quiet: true })
      .then((res) => {
        if (epoch.current === asked) apply(res)
      })
      .catch((err: unknown) => {
        if (epoch.current === asked && err instanceof ApiError && err.isUnauthenticated) forget(err)
      })
  }, [apply, forget])

  // A learner who signed in on another device has ended this one's session
  // (one session at a time), but a page left open does not know until it next
  // asks the server something — which may be long after the learner has come
  // back to it and started typing. So while somebody is signed in, /auth/me
  // is asked again whenever the tab is looked at (it gains focus, or becomes
  // visible), at most once every RECHECK_EVERY_MS.
  const signedIn = user !== null
  useEffect(() => {
    if (!signedIn) return
    let last = Date.now()
    const onLooked = () => {
      if (document.visibilityState !== 'visible') return
      const now = Date.now()
      if (now - last < RECHECK_EVERY_MS) return
      last = now
      recheck()
    }
    window.addEventListener('focus', onLooked)
    document.addEventListener('visibilitychange', onLooked)
    return () => {
      window.removeEventListener('focus', onLooked)
      document.removeEventListener('visibilitychange', onLooked)
    }
  }, [signedIn, recheck])

  // Another tab of this browser recorded (or cleared) why the session ended.
  // Every tab shares the one session cookie, so the news is this tab's too: a
  // tab on the sign-in page says it at once, and a tab still showing somebody
  // signed in asks the server rather than taking another tab's word — an
  // answer from before a newer sign-in in this browser would otherwise throw
  // out a session that is alive. Asking cannot echo back here: a dead session
  // only records the reason already stored, which writes nothing, and a live
  // one clears it once (apply), which the tab that wrote it merely takes in.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      // A null key: the other tab cleared the whole storage.
      if (e.key !== null && e.key !== SIGNED_OUT_REASON) return
      const reason = readReason()
      setSignedOutReason(reason)
      if (reason === 'replaced' && signedIn) recheck()
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [signedIn, recheck])

  const signIn = useCallback(async (login: string, password: string) => {
    // `quiet` again, for the opposite reason: a wrong password is answered 401,
    // and that 401 belongs to the form in front of the person typing. Left
    // loud, every typo would fire the global "you have been signed out" path.
    //
    // The login is trimmed — surrounding space is what a paste leaves behind —
    // and the password is sent exactly as typed: trimming it would sign
    // somebody in under a password that is not the one they set.
    const res = await api<Identity>('/auth/login', {
      method: 'POST',
      body: { login: login.trim(), password },
      quiet: true,
    })
    turn()
    apply(res)
    return res.user
  }, [turn, apply])

  const register = useCallback(async (input: RegisterInput) => {
    // The number is normalised on the way out as well as in the form, because
    // this is the only path every caller passes through. An empty username is
    // left out rather than sent: the server treats both as "none", and an
    // absent key says so more plainly.
    const username = input.username?.trim() ?? ''
    // A password the learner has just chosen themselves is never one they
    // must replace; the server clears the flag when it claims the row.
    const res = await post<Identity>('/auth/register', {
      full_name: input.full_name.trim(),
      phone: normalise(input.phone),
      ...(username ? { username } : {}),
      password: input.password,
    })
    turn()
    apply(res)
    return res.user
  }, [turn, apply])

  const signOut = useCallback(
    async (cause?: unknown) => {
      // The server has already ended the session — a quiet call came back
      // 401 — so there is nothing to sign out of there, only here, and the
      // refusal says why (a sign-in on another device is worth saying).
      if (cause instanceof ApiError && cause.isUnauthenticated) {
        forget(cause)
        return
      }
      try {
        await post('/auth/logout')
        // The server accepted it, so the session was alive until now: a reason
        // still kept from an older one (an answer that arrived late, from
        // before the last sign-in) no longer explains anything.
        remember(null)
      } finally {
        // Whether or not the server acknowledged, this browser is signed out.
        forget()
      }
    },
    [forget, remember],
  )

  const value = useMemo<Auth>(
    () => ({ user, mustChangePassword, ready, signedOutReason, signIn, register, signOut, refresh, passwordChanged }),
    [user, mustChangePassword, ready, signedOutReason, signIn, register, signOut, refresh, passwordChanged],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): Auth {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}

/**
 * How long a kept reason is worth telling. The notice explains why this
 * browser was signed out; a day later, on a shared computer, it would only
 * mislead whoever sits down next.
 */
const REASON_TTL_MS = 24 * 60 * 60 * 1000

/** The reason as stored: "replaced:<when, ms since the epoch>". */
function storedReason(raw: string | null): { reason: SignedOutReason; at: number } | null {
  const m = raw?.match(/^replaced:(\d{1,15})$/)
  if (!m) return null
  return { reason: 'replaced', at: Number(m[1]) }
}

/** The reason this browser kept, if any and still fresh; nothing when storage is switched off. */
function readReason(): SignedOutReason | null {
  try {
    const raw = localStorage.getItem(SIGNED_OUT_REASON)
    const kept = storedReason(raw)
    if (kept && Date.now() - kept.at < REASON_TTL_MS && kept.at <= Date.now() + 60_000) return kept.reason
    // Stale, from the future, or in an older shape: forget it.
    if (raw !== null) localStorage.removeItem(SIGNED_OUT_REASON)
    return null
  } catch {
    return null
  }
}

/**
 * Keeps the reason for the whole browser, writing only when it changes: the
 * same value again would tell the other tabs nothing (browsers fire no event
 * for it, but this does not lean on that), and removing an absent key is no
 * change either. A reason already kept and still fresh keeps its time, so a
 * dead cookie that is answered again and again does not stretch the notice.
 */
function keepReason(reason: SignedOutReason | null) {
  try {
    if (reason === null) {
      if (localStorage.getItem(SIGNED_OUT_REASON) !== null) localStorage.removeItem(SIGNED_OUT_REASON)
    } else if (readReason() !== reason) {
      localStorage.setItem(SIGNED_OUT_REASON, `${reason}:${Date.now()}`)
    }
  } catch {
    // Storage switched off or full: the reason lasts as long as the page does.
  }
}

/**
 * Accepts what people actually type — spaces, dashes, brackets, a leading 8 or
 * a bare nine-digit national number — and produces the +998XXXXXXXXX the API
 * expects. Anything that is already international is left alone.
 */
export function normalise(input: string): string {
  const digits = input.replace(/[^\d+]/g, '')
  if (digits.startsWith('+')) return digits
  const bare = digits.replace(/^8/, '')
  if (bare.length === 9) return `+998${bare}`
  if (bare.startsWith('998')) return `+${bare}`
  return `+${bare}`
}
